/**
 * FeedbackDetailModal - 피드백 상세 모달
 *
 * - 제출 내용 (제목, 설명, 스크린샷, 환경정보)
 * - 메시지 스레드 (AI/관리자 응답, 사용자 답변)
 * - 메시지 입력 영역
 */

import { useEffect, useState, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
  Bug, Lightbulb,
  Send, Loader2, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import type { Feedback, FeedbackMessage } from '../../models/Feedback';
import { FEEDBACK_STATUS_LABELS } from '../../models/Feedback';

const STATUS_COLORS: Record<string, string> = {
  submitted: 'bg-blue-500/10 text-blue-600',
  reviewing: 'bg-yellow-500/10 text-yellow-600',
  resolving: 'bg-orange-500/10 text-orange-600',
  unresolved: 'bg-red-500/10 text-red-600',
  resolved: 'bg-green-500/10 text-green-600',
};

interface Props {
  feedbackId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDetailModal({ feedbackId, open, onOpenChange }: Props) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!feedbackId || !open) return;
    setLoading(true);
    const unsub = Fire.feedback.subscribeFeedback(feedbackId, (fb) => {
      setFeedback(fb);
      setLoading(false);
    });
    return () => unsub();
  }, [feedbackId, open]);

  // 새 메시지 시 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [feedback?.messages.length]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !feedbackId) return;
    setSending(true);
    try {
      await Fire.feedback.addMessage(feedbackId, {
        content: newMessage.trim(),
        authorId: My.user.id,
        authorName: My.user.name,
        authorRole: 'user',
      });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const isBug = feedback?.type === 'bug';
  const statusLabel = feedback ? i18n._(FEEDBACK_STATUS_LABELS[feedback.status]) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[85vh] overflow-y-auto p-0">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !feedback ? (
          <div className="text-center py-12 text-muted-foreground">
            <Trans>Feedback not found</Trans>
          </div>
        ) : (
          <div className="space-y-5 p-6">
            {/* 헤더 */}
            <DialogHeader className="space-y-3">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${isBug ? 'bg-red-500/10' : 'bg-yellow-500/10'}`}>
                  {isBug ? <Bug className="w-5 h-5 text-red-500" /> : <Lightbulb className="w-5 h-5 text-yellow-500" />}
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-bold text-foreground">{feedback.title}</DialogTitle>
                  <DialogDescription asChild>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[feedback.status] || ''}`}>
                        {statusLabel}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(feedback.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {feedback.description && (
              <p className="text-sm text-foreground whitespace-pre-wrap">{feedback.description}</p>
            )}

            {/* 태그 */}
            {feedback.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {feedback.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* 스크린샷 */}
            {feedback.screenshots.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4" />
                  <Trans>Screenshots</Trans>
                </h3>
                <div className="flex flex-wrap gap-3">
                  {feedback.screenshots.map((entry, i) => {
                    const url = entry.imageUrl;
                    return (
                      <button
                        key={i}
                        onClick={() => setExpandedImage(expandedImage === url ? null : url)}
                        className="w-32 h-24 rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors"
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
                {expandedImage && (
                  <div className="rounded-lg overflow-hidden border border-border">
                    <img src={expandedImage} alt="" className="w-full max-h-[400px] object-contain bg-muted" />
                  </div>
                )}
              </div>
            )}



            {/* 메시지 스레드 */}
            {feedback.messages.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground"><Trans>Messages</Trans></h3>
                {feedback.messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} isMe={msg.authorId === My.user.id} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* 메시지 입력 */}
            {feedback.status !== 'resolved' && (
              <div className="flex gap-2 items-end">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t`Type a message...`}
                  rows={2}
                  className="flex-1 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="h-10"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── 메시지 버블 ──
function MessageBubble({ message, isMe }: { message: FeedbackMessage; isMe: boolean }) {
  const roleLabel = {
    user: '',
    admin: '🛡️ Admin',
    ai: '🤖 AI',
  };

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${
        isMe
          ? 'bg-primary text-primary-foreground'
          : message.authorRole === 'ai'
            ? 'bg-muted border border-border'
            : 'bg-accent'
      }`}>
        {!isMe && (
          <div className="text-xs font-medium mb-1 opacity-70">
            {roleLabel[message.authorRole]} {message.authorName}
          </div>
        )}
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        <div className={`text-meta-sm mt-1 ${isMe ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

export default FeedbackDetailModal;
